/**
 * VideoCompositor - Canvas-based video composition engine
 * 
 * Renders video frames with text overlays to match FFmpeg output exactly.
 * Inspired by Omniclip's browser-based rendering approach.
 * 
 * Video loading strategy:
 * - Server-proxied previews (/api/ranking/preview/*): Full video playback
 * - Local file uploads (blob:): Full video playback
 * - External URLs (YouTube, TikTok, etc.): Thumbnail-based preview only (CORS restricted)
 * 
 * Final rendering always uses backend FFmpeg for high-quality output.
 */

export class VideoCompositor {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.width = options.width || 1080;
    this.height = options.height || 1920;

    // Set canvas dimensions
    this.canvas.width = this.width;
    this.canvas.height = this.height;

    // Composition state
    this.clips = [];
    this.currentClipIndex = 0;
    this.currentTime = 0;
    this.isPlaying = false;
    this.animationFrameId = null;

    // Global settings
    this.mainTitle = '';
    this.titleStyle = {};
    this.backgroundColor = '#000000';
    this.backgroundAudio = null;

    // Transition settings
    this.transitionDuration = 0.5; // seconds
    this.transitionType = 'fade';

    // Audio settings
    this.isMuted = false; // Start unmuted to hear audio
    this.volume = 1.0;

    // Callbacks
    this.onTimeUpdate = null;
    this.onClipChange = null;
    this.onPlayStateChange = null;

    // Video element pool for preloading
    this.videoElements = new Map();

    // Track URLs we've already attempted to load (prevents infinite retries)
    this.attemptedUrls = new Set();

    // Thumbnail image cache
    this.thumbnailCache = new Map();

    // Font loading status
    this.fontsLoaded = false;
    this._loadFonts();

    // Initial render
    this._renderPlaceholder();
  }

  /**
   * Update the canvas element reference (when React replaces the DOM element)
   */
  updateCanvas(newCanvas) {
    if (!newCanvas) return;

    // Canvas reference updated

    this.canvas = newCanvas;
    this.ctx = newCanvas.getContext('2d');

    // Ensure dimensions are set
    this.canvas.width = this.width;
    this.canvas.height = this.height;

    // Re-render current frame
    this.renderFrame();
  }

  /**
   * Load custom fonts to match FFmpeg output
   */
  async _loadFonts() {
    const fonts = [
      { name: 'Archivo Black', url: 'https://fonts.googleapis.com/css2?family=Archivo+Black&display=swap' },
      { name: 'Inter', url: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap' },
      { name: 'Roboto', url: 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;700;900&display=swap' },
      { name: 'Montserrat', url: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700;800;900&display=swap' },
    ];

    try {
      // Load fonts via CSS
      for (const font of fonts) {
        if (!document.querySelector(`link[href*="${font.name.replace(' ', '+')}"]`)) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = font.url;
          document.head.appendChild(link);
        }
      }

      // Wait for fonts to be ready
      await document.fonts.ready;
      this.fontsLoaded = true;
    } catch (error) {
      console.warn('Font loading failed:', error);
      this.fontsLoaded = true; // Continue with system fonts
    }
  }

  /**
   * Set the composition configuration
   */
  setConfig(config) {
    this.mainTitle = config.mainTitle || '';
    this.titleStyle = config.titleStyle || {};
    this.backgroundColor = config.backgroundColor || '#000000';
    this.transitionDuration = config.transitionDuration || 0.5;
    this.transitionType = config.transitionType || 'fade';
  }

  /**
   * Add a video clip to the composition
   */
  addClip(clip) {
    const url = clip.previewUrl || clip.url;

    const clipData = {
      id: clip.id,
      url: url,
      thumbnail: clip.thumbnail || null,
      startTime: clip.startTime || 0,
      endTime: clip.endTime || 5,
      label: clip.label || '',
      style: clip.style || {},
      videoElement: null,
      loaded: false,
      loadError: null,
      loadAttempted: false,
      _thumbnailImage: null,
      duration: (clip.endTime || 5) - (clip.startTime || 0),
    };

    this.clips.push(clipData);

    // Determine if this URL can be loaded
    // Supabase URLs, blob URLs, and localhost should always be attempted
    // External URLs (YouTube, etc) should only be attempted once
    const isSupabaseUrl = url && url.includes('.supabase.co/');
    const isBlobUrl = url && url.startsWith('blob:');
    const isLocalUrl = url && (url.includes('localhost') || url.includes('127.0.0.1'));
    const shouldAlwaysRetry = isSupabaseUrl || isBlobUrl || isLocalUrl;

    if (url && (shouldAlwaysRetry || !this.attemptedUrls.has(url))) {
      if (!shouldAlwaysRetry) {
        this.attemptedUrls.add(url);
      }
      this._preloadVideo(clipData);
    } else if (url && this.attemptedUrls.has(url)) {
      // Already attempted external URL - mark as failed
      clipData.loadAttempted = true;
      clipData.loadError = 'cors';
    }

    // Always load thumbnail as fallback
    this._preloadThumbnail(clipData);

    return clipData;
  }

  /**
   * Preload thumbnail image - this is the primary preview for external videos
   */
  _preloadThumbnail(clip) {
    if (!clip.thumbnail) return;

    // Check cache first
    if (this.thumbnailCache.has(clip.thumbnail)) {
      clip._thumbnailImage = this.thumbnailCache.get(clip.thumbnail);
      this.renderFrame();
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      clip._thumbnailImage = img;
      this.thumbnailCache.set(clip.thumbnail, img);
      // Thumbnail loaded successfully
      // Always render when thumbnail loads - it's our main preview
      this.renderFrame();
    };
    img.onerror = () => {
      // Thumbnail failed - try without CORS header
      const imgNoCors = new Image();
      imgNoCors.onload = () => {
        clip._thumbnailImage = imgNoCors;
        this.thumbnailCache.set(clip.thumbnail, imgNoCors);
        this.renderFrame();
      };
      imgNoCors.onerror = () => {
        console.warn('[VideoCompositor] Thumbnail failed for clip:', clip.id);
      };
      imgNoCors.src = clip.thumbnail;
    };
    img.src = clip.thumbnail;
  }

  /**
   * Update clips from array
   * Preserves video elements when only trim/label changes (not URL)
   */
  setClips(clips) {
    const loadedClips = clips.filter(c => c.isLoaded && (c.previewUrl || c.url));

    // Check if this is just a trim/label update (same clips, same URLs)
    const newClipIds = loadedClips.map(c => c.id).join(',');
    const currentClipIds = this.clips.map(c => c.id).join(',');
    const sameClips = newClipIds === currentClipIds && this.clips.length > 0;

    if (sameClips) {
      // Just update timing/labels without recreating clips
      loadedClips.forEach(newClip => {
        const existingClip = this.clips.find(c => c.id === newClip.id);
        if (existingClip) {
          // Update only the changeable properties, preserve video element
          existingClip.startTime = newClip.startTime || 0;
          existingClip.endTime = newClip.endTime || 5;
          existingClip.duration = (newClip.endTime || 5) - (newClip.startTime || 0);
          existingClip.label = newClip.label || '';
          existingClip.style = newClip.style || {};
        }
      });
      this._calculateTimeline();
      this.renderFrame();
      return;
    }

    // Different clips - need to rebuild
    // Use the stable this.videoElements Map (keyed by clip ID) to preserve video elements
    // This survives across setClips calls even when clips are removed/re-added



    this.clips = [];

    // Add new clips, reusing video elements from the stable cache
    loadedClips.forEach(clip => {
      const url = clip.previewUrl || clip.url;

      // Look up existing video element by clip ID in the stable cache
      const existingVideo = this.videoElements.get(clip.id);
      const existingLoaded = existingVideo && existingVideo.readyState >= 2; // HAVE_CURRENT_DATA or better



      const clipData = {
        id: clip.id,
        url: url,
        thumbnail: clip.thumbnail || null,
        startTime: clip.startTime || 0,
        endTime: clip.endTime || 5,
        label: clip.label || '',
        style: clip.style || {},
        videoElement: existingLoaded ? existingVideo : null,
        loaded: existingLoaded ? true : false,
        loadError: null,
        loadAttempted: existingLoaded ? true : false,
        _thumbnailImage: this.thumbnailCache.get(clip.thumbnail) || null,
        duration: (clip.endTime || 5) - (clip.startTime || 0),
      };

      this.clips.push(clipData);

      // Load video if not already loaded (or not ready enough to play)
      // Supabase/blob/localhost URLs should always retry, external URLs only once
      const isSupabaseUrl = url && url.includes('.supabase.co/');
      const isBlobUrl = url && url.startsWith('blob:');
      const isLocalUrl = url && (url.includes('localhost') || url.includes('127.0.0.1'));
      const shouldAlwaysRetry = isSupabaseUrl || isBlobUrl || isLocalUrl;

      if (!existingLoaded && url && (shouldAlwaysRetry || !this.attemptedUrls.has(url))) {
        if (!shouldAlwaysRetry) {
          this.attemptedUrls.add(url);
        }
        this._preloadVideo(clipData);
      }

      // Load thumbnail if not cached
      if (!clipData._thumbnailImage && clip.thumbnail) {
        this._preloadThumbnail(clipData);
      }
    });

    this._calculateTimeline();
    this.renderFrame();
  }

  /**
   * Calculate timeline positions for all clips
   */
  _calculateTimeline() {
    let currentPosition = 0;

    this.clips.forEach((clip, index) => {
      clip.timelineStart = currentPosition;
      clip.timelineEnd = currentPosition + clip.duration;

      // Account for transition overlap (except for last clip)
      if (index < this.clips.length - 1) {
        currentPosition += clip.duration - this.transitionDuration;
      } else {
        currentPosition += clip.duration;
      }
    });

    this.totalDuration = currentPosition;
  }

  /**
   * Preload video element for a clip
   * Note: Many URLs (YouTube, TikTok) won't work directly due to CORS/DRM
   * We gracefully handle failures and show thumbnails/placeholders instead
   */
  async _preloadVideo(clip) {
    if (!clip.url) return null;

    // Check if this is a known external URL that won't work
    const isExternalUrl = this._isExternalVideoUrl(clip.url);
    if (isExternalUrl) {
      // Skip loading attempt for external URLs - use thumbnail instead
      clip.loadAttempted = true;
      clip.loadError = 'external';
      this.renderFrame();
      return null;
    }

    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.muted = this.isMuted; // Use compositor mute state
    video.playsInline = true;
    video.preload = 'auto';
    video.volume = this.volume;

    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        // Timeout after 15 seconds - video likely can't buffer fully
        console.warn(`[VideoCompositor] ⏱️ Timeout loading clip ${clip.id}`);
        clip.loadAttempted = true;
        clip.loaded = false;
        clip.loadError = 'timeout';
        this.renderFrame();
        resolve(null);
      }, 15000);

      // Use canplaythrough instead of loadeddata - ensures video can actually play
      video.oncanplaythrough = () => {
        clearTimeout(timeout);
        clip.videoElement = video;
        clip.loaded = true;
        clip.loadAttempted = true;
        clip.loadError = null;
        this.videoElements.set(clip.id, video);
        this.renderFrame();
        resolve(video);
      };

      video.onerror = () => {
        clearTimeout(timeout);
        console.error(`[VideoCompositor] ❌ Error loading clip ${clip.id}`);
        // Mark as attempted and failed - won't retry
        clip.loadAttempted = true;
        clip.loaded = false;
        clip.loadError = 'cors';
        clip.videoElement = null;
        this.renderFrame();
        resolve(null);
      };

      try {
        video.src = clip.url;
        video.load();
      } catch (e) {
        clearTimeout(timeout);
        clip.loadAttempted = true;
        clip.loaded = false;
        clip.loadError = 'invalid';
        this.renderFrame();
        resolve(null);
      }
    });
  }

  /**
   * Check if URL is an external video service that won't work with direct loading
   * Server-proxied preview URLs (/api/ranking/preview/) are NOT external
   */
  _isExternalVideoUrl(url) {
    if (!url) return false;

    // Server-proxied preview URLs are safe to load directly
    // These come as full URLs like http://localhost:5001/api/ranking/preview/...
    if (url.includes('/api/ranking/preview/')) {
      return false;
    }

    // Local blob URLs are safe (for file uploads)
    if (url.startsWith('blob:')) {
      return false;
    }

    // File uploads are safe
    if (url.startsWith('file:')) {
      return false;
    }

    // Data URLs are safe (for embedded media)
    if (url.startsWith('data:')) {
      return false;
    }

    // Supabase storage URLs are safe (signed preview URLs)
    if (url.includes('.supabase.co/')) {
      return false;
    }

    // Localhost URLs are safe (for development)
    if (url.includes('localhost') || url.includes('127.0.0.1')) {
      return false;
    }

    const externalPatterns = [
      /youtube\.com/i,
      /youtu\.be/i,
      /tiktok\.com/i,
      /instagram\.com/i,
      /facebook\.com/i,
      /twitter\.com/i,
      /x\.com/i,
      /vimeo\.com/i,
      /twitch\.tv/i,
    ];
    return externalPatterns.some(pattern => pattern.test(url));
  }

  /**
   * Get the clip at a specific timeline time
   */
  getClipAtTime(time) {
    for (let i = 0; i < this.clips.length; i++) {
      const clip = this.clips[i];
      if (time >= clip.timelineStart && time < clip.timelineEnd) {
        return { clip, index: i };
      }
    }

    // Return last clip if past end
    if (this.clips.length > 0) {
      return { clip: this.clips[this.clips.length - 1], index: this.clips.length - 1 };
    }

    return null;
  }

  /**
   * Check if we're in a transition zone
   */
  getTransitionState(time) {
    for (let i = 0; i < this.clips.length - 1; i++) {
      const currentClip = this.clips[i];
      const nextClip = this.clips[i + 1];

      const transitionStart = currentClip.timelineEnd - this.transitionDuration;
      const transitionEnd = currentClip.timelineEnd;

      if (time >= transitionStart && time < transitionEnd) {
        const progress = (time - transitionStart) / this.transitionDuration;
        return {
          inTransition: true,
          fromClip: currentClip,
          toClip: nextClip,
          progress: progress,
          fromIndex: i,
          toIndex: i + 1,
        };
      }
    }

    return { inTransition: false };
  }

  /**
   * Seek to a specific time
   */
  seek(time) {
    this.currentTime = Math.max(0, Math.min(time, this.totalDuration || 0));

    const clipInfo = this.getClipAtTime(this.currentTime);
    if (clipInfo) {
      this.currentClipIndex = clipInfo.index;

      // Seek video element
      const clip = clipInfo.clip;
      if (clip.videoElement) {
        const clipLocalTime = this.currentTime - clip.timelineStart + clip.startTime;
        clip.videoElement.currentTime = clipLocalTime;
      }
    }

    this.renderFrame();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(this.currentTime, this.totalDuration);
    }
  }

  /**
   * Start playback
   */
  play() {
    if (this.isPlaying) return;

    // Don't play if there's nothing to play
    if (this.clips.length === 0) {
      console.warn('[VideoCompositor] Cannot play - no clips loaded');
      if (this.onPlayStateChange) {
        this.onPlayStateChange(false);
      }
      return;
    }

    // If duration is 0 but we have clips, recalculate
    if (this.totalDuration <= 0) {
      this._calculateTimeline();
    }

    // If still no duration, set a default based on clip count
    if (this.totalDuration <= 0) {
      this.totalDuration = this.clips.length * 5; // 5 seconds per clip
    }

    // Starting playback

    this.isPlaying = true;
    this.lastFrameTime = performance.now();

    // Start only the current clip's video (not all clips)
    const clipInfo = this.getClipAtTime(this.currentTime);
    if (clipInfo && clipInfo.clip.videoElement && clipInfo.clip.loaded) {
      const video = clipInfo.clip.videoElement;
      video.muted = this.isMuted;
      video.volume = this.volume;
      // Sync time
      const clipLocalTime = this.currentTime - clipInfo.clip.timelineStart + clipInfo.clip.startTime;
      video.currentTime = clipLocalTime;
      video.play().catch(() => { });
    }

    this._animate();

    if (this.onPlayStateChange) {
      this.onPlayStateChange(true);
    }
  }

  /**
   * Pause playback
   */
  pause() {
    this.isPlaying = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Pause video elements
    this.clips.forEach(clip => {
      if (clip.videoElement) {
        clip.videoElement.pause();
      }
    });
  }

  /**
   * Set mute state
   */
  setMuted(muted) {
    this.isMuted = muted;
    this.clips.forEach(clip => {
      if (clip.videoElement) {
        clip.videoElement.muted = muted;
      }
    });
  }

  /**
   * Set volume (0-1)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    this.clips.forEach(clip => {
      if (clip.videoElement) {
        clip.videoElement.volume = this.volume;
      }
    });

    if (this.onPlayStateChange) {
      this.onPlayStateChange(false);
    }
  }

  /**
   * Toggle play/pause with debounce protection
   */
  togglePlay() {
    // Debounce - ignore rapid toggles (within 200ms)
    const now = Date.now();
    if (this._lastToggleTime && (now - this._lastToggleTime) < 200) {
      // Debouncing rapid toggle
      return;
    }
    this._lastToggleTime = now;

    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Animation loop - works with both video and thumbnail-based preview
   */
  _animate() {
    if (!this.isPlaying) {
      // Animation stopped
      return;
    }

    const now = performance.now();
    const delta = (now - this.lastFrameTime) / 1000;
    this.lastFrameTime = now;

    this.currentTime += delta;

    // Track current second for clip changes
    if (Math.floor(this.currentTime) !== this._lastLoggedSecond) {
      this._lastLoggedSecond = Math.floor(this.currentTime);
    }

    // Loop at end
    if (this.totalDuration > 0 && this.currentTime >= this.totalDuration) {
      // Looping back to start
      this.currentTime = 0;
      // Reset all video elements to start
      this.clips.forEach(clip => {
        if (clip.videoElement && clip.loaded) {
          clip.videoElement.currentTime = clip.startTime;
        }
      });
    }

    // Update current clip index and manage video playback
    const clipInfo = this.getClipAtTime(this.currentTime);
    if (clipInfo) {
      // Handle clip change
      if (clipInfo.index !== this.currentClipIndex) {
        const previousIndex = this.currentClipIndex;

        // Pause previous clip's video
        if (previousIndex >= 0 && previousIndex < this.clips.length) {
          const prevClip = this.clips[previousIndex];
          if (prevClip.videoElement && prevClip.loaded) {
            prevClip.videoElement.pause();
          }
        }

        this.currentClipIndex = clipInfo.index;

        // Start current clip's video
        const currentClip = clipInfo.clip;
        if (currentClip.videoElement && currentClip.loaded) {
          currentClip.videoElement.muted = this.isMuted;
          currentClip.videoElement.volume = this.volume;
          currentClip.videoElement.play().catch(() => { });
        }

        if (this.onClipChange) {
          this.onClipChange(clipInfo.index, previousIndex);
        }
      }

      // Sync video time with timeline
      const currentClip = clipInfo.clip;
      if (currentClip.videoElement && currentClip.loaded) {
        const clipLocalTime = this.currentTime - currentClip.timelineStart + currentClip.startTime;
        // Only sync if significantly out of sync (>0.3s) to avoid stuttering
        if (Math.abs(currentClip.videoElement.currentTime - clipLocalTime) > 0.3) {
          currentClip.videoElement.currentTime = clipLocalTime;
        }
      }
    }

    this.renderFrame();

    if (this.onTimeUpdate) {
      this.onTimeUpdate(this.currentTime, this.totalDuration || 0);
    }

    this.animationFrameId = requestAnimationFrame(() => this._animate());
  }

  /**
   * Render a single frame
   */
  renderFrame() {
    // Verify canvas context is valid
    if (!this.ctx || !this.canvas) {
      console.error('[VideoCompositor] Canvas or context is null!');
      return;
    }

    // Check if canvas is still in DOM - if not, skip this frame
    // (React may temporarily replace the canvas during updates)
    if (!this.canvas.isConnected) {
      console.warn('[VideoCompositor] Canvas is not connected to DOM, skipping frame render');
      return;
    }

    // Ensure canvas dimensions match
    if (this.canvas.width !== this.width || this.canvas.height !== this.height) {
      // Fixing canvas dimensions
      this.canvas.width = this.width;
      this.canvas.height = this.height;
    }

    const ctx = this.ctx;

    // Clear canvas with background color
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(0, 0, this.width, this.height);

    if (this.clips.length === 0) {
      this._renderPlaceholder();
      return;
    }

    // Check for transition
    const transitionState = this.getTransitionState(this.currentTime);

    if (transitionState.inTransition) {
      this._renderTransition(transitionState);
    } else {
      const clipInfo = this.getClipAtTime(this.currentTime);
      if (clipInfo) {
        this._renderClip(clipInfo.clip, clipInfo.index);
      } else {
        this._renderPlaceholder();
      }
    }

    // Render overlays (title, rank list)
    this._renderOverlays();

    // Show preview overlay when playing
    if (this.isPlaying) {
      this._renderDebugInfo();
    }
  }

  /**
   * Render playback info overlay (shows current time and preview mode indicator)
   */
  _renderDebugInfo() {
    const ctx = this.ctx;
    const timeStr = this.currentTime.toFixed(1) + 's / ' + (this.totalDuration || 0).toFixed(1) + 's';

    // Top bar - Preview mode indicator
    ctx.fillStyle = 'rgba(0, 0, 0, 0.75)';
    ctx.fillRect(0, 0, this.width, 70);

    // "PREVIEW" badge
    ctx.fillStyle = '#eeba2c';
    ctx.font = 'bold 24px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('▶ PREVIEW', 25, 35);

    // Time counter
    ctx.fillStyle = '#d4a322';
    ctx.font = 'bold 28px "JetBrains Mono", monospace';
    ctx.textAlign = 'right';
    ctx.fillText(timeStr, this.width - 25, 35);

    // Animated progress bar at top
    const progress = this.totalDuration > 0 ? this.currentTime / this.totalDuration : 0;
    ctx.fillStyle = 'rgba(238, 186, 44, 0.2)';
    ctx.fillRect(0, 60, this.width, 10);
    ctx.fillStyle = '#d4a322';
    ctx.fillRect(0, 60, this.width * progress, 10);
  }

  /**
   * Render placeholder when no clips
   */
  _renderPlaceholder() {
    const ctx = this.ctx;
    ctx.fillStyle = '#161616';
    ctx.fillRect(0, 0, this.width, this.height);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.font = '48px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Import videos to preview', this.width / 2, this.height / 2);
  }

  /**
   * Render a single clip
   */
  _renderClip(clip, index) {
    if (clip.videoElement && clip.loaded) {
      // Calculate local time within clip
      const localTime = this.currentTime - clip.timelineStart + clip.startTime;

      // Sync video element if needed
      if (Math.abs(clip.videoElement.currentTime - localTime) > 0.1) {
        clip.videoElement.currentTime = localTime;
      }

      // Debug: Check video dimensions
      if (clip.videoElement.videoWidth === 0 || clip.videoElement.videoHeight === 0) {
        console.warn(`[VideoCompositor] Clip ${index} video has 0 dimensions, drawing thumbnail instead`);
        if (clip._thumbnailImage) {
          this._drawImageFit(clip._thumbnailImage);
        }
        return;
      }

      // Draw video frame scaled to fill canvas (9:16 aspect)
      this._drawVideoFit(clip.videoElement);
    } else if (clip._thumbnailImage) {
      // Show thumbnail image as the preview (primary case for external videos)
      this._drawImageFit(clip._thumbnailImage);

      // Show a subtle indicator that this is a preview
      this._renderPreviewIndicator(index, clip.label);
    } else {
      // Placeholder when neither video nor thumbnail available
      this._renderClipPlaceholder(clip, index);
    }
  }

  /**
   * Render preview indicator overlay on thumbnail
   */
  _renderPreviewIndicator(index, label) {
    const ctx = this.ctx;

    // Small semi-transparent badge at bottom
    const badgeHeight = 60;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
    ctx.fillRect(0, this.height - badgeHeight, this.width, badgeHeight);

    // Clip number
    ctx.fillStyle = '#d4a322';
    ctx.font = 'bold 24px Inter, sans-serif';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';

    // Countdown ranking: Total - Index
    const rankNum = this.clips.length - index;
    ctx.fillText(`#${rankNum}`, 20, this.height - badgeHeight / 2);

    // Label (truncated if too long)
    const maxLabelWidth = this.width - 100;
    ctx.fillStyle = '#ffffff';
    ctx.font = '20px Inter, sans-serif';
    ctx.textAlign = 'left';
    let displayLabel = label || `Clip ${index + 1}`;
    while (ctx.measureText(displayLabel).width > maxLabelWidth && displayLabel.length > 10) {
      displayLabel = displayLabel.slice(0, -4) + '...';
    }
    ctx.fillText(displayLabel, 70, this.height - badgeHeight / 2);
  }

  /**
   * Render placeholder when no video or thumbnail
   */
  _renderClipPlaceholder(clip, index) {
    const ctx = this.ctx;

    // Dark gradient background
    ctx.fillStyle = '#161616';
    ctx.fillRect(0, 0, this.width, this.height);

    // Gradient overlay
    const gradient = ctx.createLinearGradient(0, 0, 0, this.height);
    gradient.addColorStop(0, 'rgba(238, 186, 44, 0.1)');
    gradient.addColorStop(1, 'rgba(238, 186, 44, 0.1)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, this.width, this.height);

    // Clip number badge
    ctx.fillStyle = 'rgba(238, 186, 44, 0.8)';
    ctx.font = '120px "Archivo Black", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    // Countdown ranking
    const rankNum = this.clips.length - index;
    ctx.fillText(`${rankNum}`, this.width / 2, this.height / 2 - 50);

    // Label
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.font = '24px Inter, sans-serif';
    ctx.fillText(clip.label || `Clip ${index + 1}`, this.width / 2, this.height / 2 + 50);

    // Status message
    ctx.font = '16px Inter, sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillText('Loading thumbnail...', this.width / 2, this.height / 2 + 90);
  }

  /**
   * Draw an image to fit canvas while maintaining aspect ratio
   */
  _drawImageFit(image) {
    const ctx = this.ctx;
    const imageAspect = image.width / image.height;
    const canvasAspect = this.width / this.height;

    let drawWidth, drawHeight, drawX, drawY;

    if (imageAspect > canvasAspect) {
      drawHeight = this.height;
      drawWidth = this.height * imageAspect;
      drawX = (this.width - drawWidth) / 2;
      drawY = 0;
    } else {
      drawWidth = this.width;
      drawHeight = this.width / imageAspect;
      drawX = 0;
      drawY = (this.height - drawHeight) / 2;
    }

    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  }

  /**
   * Draw video to fit canvas while maintaining aspect ratio
   */
  _drawVideoFit(video) {
    const ctx = this.ctx;
    const videoAspect = video.videoWidth / video.videoHeight;
    const canvasAspect = this.width / this.height;

    let drawWidth, drawHeight, drawX, drawY;

    if (videoAspect > canvasAspect) {
      // Video is wider - fit to height, crop sides
      drawHeight = this.height;
      drawWidth = this.height * videoAspect;
      drawX = (this.width - drawWidth) / 2;
      drawY = 0;
    } else {
      // Video is taller - fit to width, crop top/bottom
      drawWidth = this.width;
      drawHeight = this.width / videoAspect;
      drawX = 0;
      drawY = (this.height - drawHeight) / 2;
    }

    ctx.drawImage(video, drawX, drawY, drawWidth, drawHeight);
  }

  /**
   * Render transition between clips
   */
  _renderTransition(state) {
    const { fromClip, toClip, progress, fromIndex, toIndex } = state;

    switch (this.transitionType) {
      case 'fade':
        this._renderFadeTransition(fromClip, toClip, progress, fromIndex, toIndex);
        break;
      case 'slideLeft':
        this._renderSlideTransition(fromClip, toClip, progress, 'left', fromIndex, toIndex);
        break;
      case 'slideRight':
        this._renderSlideTransition(fromClip, toClip, progress, 'right', fromIndex, toIndex);
        break;
      case 'wipeLeft':
        this._renderWipeTransition(fromClip, toClip, progress, 'left', fromIndex, toIndex);
        break;
      case 'wipeRight':
        this._renderWipeTransition(fromClip, toClip, progress, 'right', fromIndex, toIndex);
        break;
      default:
        this._renderFadeTransition(fromClip, toClip, progress, fromIndex, toIndex);
    }
  }

  /**
   * Helper to draw a clip (video, thumbnail, or placeholder)
   */
  _drawClipContent(clip, index) {
    if (clip.videoElement && clip.loaded) {
      this._drawVideoFit(clip.videoElement);
    } else if (clip._thumbnailImage) {
      this._drawImageFit(clip._thumbnailImage);
    } else {
      this._renderClipPlaceholder(clip, index);
    }
  }

  /**
   * Crossfade transition
   */
  _renderFadeTransition(fromClip, toClip, progress, fromIndex, toIndex) {
    const ctx = this.ctx;

    // Draw "from" clip
    ctx.globalAlpha = 1 - progress;
    this._drawClipContent(fromClip, fromIndex);

    // Draw "to" clip
    ctx.globalAlpha = progress;
    this._drawClipContent(toClip, toIndex);

    ctx.globalAlpha = 1;
  }

  /**
   * Slide transition
   */
  _renderSlideTransition(fromClip, toClip, progress, direction, fromIndex, toIndex) {
    const ctx = this.ctx;
    const offset = direction === 'left' ? -this.width * progress : this.width * progress;

    // Draw "from" clip sliding out
    ctx.save();
    ctx.translate(offset, 0);
    this._drawClipContent(fromClip, fromIndex);
    ctx.restore();

    // Draw "to" clip sliding in
    ctx.save();
    const inOffset = direction === 'left' ? this.width + offset : -this.width + offset;
    ctx.translate(inOffset, 0);
    this._drawClipContent(toClip, toIndex);
    ctx.restore();
  }

  /**
   * Wipe transition
   */
  _renderWipeTransition(fromClip, toClip, progress, direction, fromIndex, toIndex) {
    const ctx = this.ctx;

    // Draw "from" clip full
    this._drawClipContent(fromClip, fromIndex);

    // Draw "to" clip with clipping
    ctx.save();
    ctx.beginPath();
    if (direction === 'left') {
      ctx.rect(0, 0, this.width * progress, this.height);
    } else {
      ctx.rect(this.width * (1 - progress), 0, this.width * progress, this.height);
    }
    ctx.clip();

    this._drawClipContent(toClip, toIndex);
    ctx.restore();
  }

  /**
   * Render all overlays (title, rankings)
   */
  _renderOverlays() {
    this._renderMainTitle();
    this._renderRankings();
  }

  /**
   * Parse HTML content into renderable segments
   * Each segment has: text, color, bold, italic, backgroundColor, fontSize
   */
  _parseHtmlToSegments(html) {
    if (!html) return [];

    const segments = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    const processNode = (node, inheritedStyle = {}) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent;
        if (text) {
          segments.push({
            text,
            ...inheritedStyle
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node;
        const tagName = element.tagName.toLowerCase();

        // Clone inherited style and modify based on element
        const newStyle = { ...inheritedStyle };

        // Handle <br> as line break
        if (tagName === 'br') {
          segments.push({ text: '\n', lineBreak: true });
          return;
        }

        // Handle bold
        if (tagName === 'b' || tagName === 'strong') {
          newStyle.bold = true;
        }

        // Handle italic
        if (tagName === 'i' || tagName === 'em') {
          newStyle.italic = true;
        }

        // Handle inline styles
        const inlineColor = element.style.color;
        if (inlineColor) {
          newStyle.color = this._normalizeColor(inlineColor);
        }

        const bgColor = element.style.backgroundColor;
        if (bgColor && bgColor !== 'transparent') {
          newStyle.backgroundColor = this._normalizeColor(bgColor);
        }

        const fontSize = element.style.fontSize;
        if (fontSize) {
          newStyle.fontSize = parseInt(fontSize);
        }

        // Handle font tags with color attribute
        if (tagName === 'font' && element.getAttribute('color')) {
          newStyle.color = element.getAttribute('color');
        }

        // Process children
        for (const child of element.childNodes) {
          processNode(child, newStyle);
        }
      }
    };

    // Process all child nodes
    for (const child of tempDiv.childNodes) {
      processNode(child);
    }

    return segments;
  }

  /**
   * Normalize color values to hex format
   */
  _normalizeColor(color) {
    if (!color) return null;

    // Already hex
    if (color.startsWith('#')) return color;

    // RGB format
    if (color.startsWith('rgb')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        const r = parseInt(match[1]).toString(16).padStart(2, '0');
        const g = parseInt(match[2]).toString(16).padStart(2, '0');
        const b = parseInt(match[3]).toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
      }
    }

    return color;
  }

  /**
   * Render main title - supports rich text HTML
   */
  _renderMainTitle() {
    if (!this.mainTitle) return;

    const ctx = this.ctx;
    const style = this.titleStyle;

    // Parse HTML to segments
    const segments = this._parseHtmlToSegments(this.mainTitle);
    if (segments.length === 0) return;

    const baseFontSize = style.fontSize || 80;
    const fontFamily = style.font || 'Archivo Black';
    const strokeWidth = style.strokeWidth || 0;
    const strokeColor = style.strokeColor || '#000000';
    const strokeOpacity = (style.strokeOpacity !== undefined ? style.strokeOpacity : 100) / 100;
    const defaultColor = '#FFFFFF';
    const verticalPosition = style.verticalPosition || 15;
    const maxWidth = (style.maxWidth || 90) / 100 * this.width;
    const textAlign = style.textAlign || 'center';

    const lineHeight = baseFontSize * 1.3;
    const startY = (verticalPosition / 100) * this.height;

    // Group segments into lines (split by line breaks)
    const lines = [];
    let currentLine = [];

    segments.forEach(segment => {
      if (segment.lineBreak || segment.text === '\n') {
        if (currentLine.length > 0) {
          lines.push(currentLine);
        }
        currentLine = [];
      } else {
        currentLine.push(segment);
      }
    });

    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Render each line
    lines.forEach((lineSegments, lineIndex) => {
      const y = startY + lineIndex * lineHeight;

      // Calculate total line width for alignment
      let totalWidth = 0;
      const segmentWidths = lineSegments.map(segment => {
        const fontSize = segment.fontSize || baseFontSize;
        const fontWeight = segment.bold ? '900' : '700';
        const fontStyle = segment.italic ? 'italic ' : '';
        ctx.font = `${fontStyle}${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
        const width = ctx.measureText(segment.text).width;
        totalWidth += width;
        return width;
      });

      // Determine starting X based on alignment
      let x;
      if (textAlign === 'left') {
        x = (this.width - maxWidth) / 2;
      } else if (textAlign === 'right') {
        x = (this.width + maxWidth) / 2 - totalWidth;
      } else {
        // Center
        x = (this.width - totalWidth) / 2;
      }

      // Render each segment in the line
      lineSegments.forEach((segment, segIndex) => {
        const segmentWidth = segmentWidths[segIndex];
        const fontSize = segment.fontSize || baseFontSize;
        const fontWeight = segment.bold ? '900' : '700';
        const fontStyle = segment.italic ? 'italic ' : '';
        const color = segment.color || defaultColor;

        ctx.font = `${fontStyle}${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        // Draw background highlight if present
        if (segment.backgroundColor) {
          const padding = 6;
          ctx.fillStyle = segment.backgroundColor;
          ctx.fillRect(
            x - padding,
            y - padding / 2,
            segmentWidth + padding * 2,
            fontSize + padding
          );
        }

        // Stroke (border)
        if (strokeWidth > 0 && strokeOpacity > 0) {
          ctx.save();
          ctx.globalAlpha = strokeOpacity;
          ctx.strokeStyle = strokeColor;
          ctx.lineWidth = strokeWidth * 2;
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;
          ctx.strokeText(segment.text, x, y);
          ctx.restore();
        }

        // Fill text
        ctx.fillStyle = color;
        ctx.fillText(segment.text, x, y);

        // Move x for next segment
        x += segmentWidth;
      });
    });
  }

  /**
   * Render ranking list overlay - LEFT aligned, MIDDLE of screen
   * Shows all rank numbers persistently, titles appear when clip plays and STAY visible
   */
  _renderRankings() {
    if (this.clips.length === 0) return;

    const ctx = this.ctx;
    const clipInfo = this.getClipAtTime(this.currentTime);
    const activeIndex = clipInfo ? clipInfo.index : 0;

    // Position: LEFT aligned, starting higher up on screen
    const leftMargin = 60;
    const lineHeight = 100; // More spacing between each rank line

    // Start at 30% from top (below the main title area)
    const startY = this.height * 0.30;

    // Render each rank number
    this.clips.forEach((clip, index) => {
      const style = clip.style || {};
      const isActive = index === activeIndex;
      // Show label if this clip has already played OR is currently playing
      const hasPlayed = index <= activeIndex;

      // Style settings from clip card
      const fontFamily = style.font || 'Archivo Black';
      const color = style.color || '#eeba2c';
      const strokeColor = style.strokeColor || '#000000';
      const strokeWidth = style.strokeWidth || 4;
      const strokeOpacity = (style.strokeOpacity !== undefined ? style.strokeOpacity : 100) / 100;
      const baseFontSize = style.fontSize || 48;
      const maxWidthPercent = style.maxWidth || 90;

      // Active clip uses full size, others are slightly smaller
      const fontSize = isActive ? baseFontSize : Math.round(baseFontSize * 0.75);
      const textOpacity = isActive ? 1.0 : (hasPlayed ? 0.7 : 0.4);
      const maxTextWidth = (maxWidthPercent / 100) * (this.width - leftMargin * 2);

      // Visual Position: Rank 1 at Top, Rank N at Bottom
      // rankNum 1 -> offset 0
      // rankNum 5 -> offset 4
      const visualRowIndex = (this.clips.length - index) - 1;
      const y = startY + visualRowIndex * lineHeight;

      ctx.save();

      ctx.font = `900 ${fontSize}px "${fontFamily}", sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';

      // Build text: show label for clips that have played, just number for future clips
      const rankNum = `${this.clips.length - index}.`;
      let text = hasPlayed ? `${rankNum} ${clip.label}` : rankNum;

      // Truncate text if it exceeds maxWidth
      if (hasPlayed && ctx.measureText(text).width > maxTextWidth) {
        while (ctx.measureText(text + '...').width > maxTextWidth && text.length > rankNum.length + 3) {
          text = text.slice(0, -1);
        }
        text = text.trim() + '...';
      }

      // Stroke (border)
      if (strokeWidth > 0 && strokeOpacity > 0) {
        ctx.globalAlpha = textOpacity * strokeOpacity;
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = isActive ? strokeWidth * 2 : strokeWidth;
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
        ctx.strokeText(text, leftMargin, y);
      }

      // Fill
      ctx.globalAlpha = textOpacity;
      ctx.fillStyle = color;
      ctx.fillText(text, leftMargin, y);

      ctx.restore();
    });
  }

  /**
   * Convert hex color to rgba
   */
  _hexToRgba(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  /**
   * Get total duration of composition
   */
  getTotalDuration() {
    return this.totalDuration || 0;
  }

  /**
   * Get current time
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * Cleanup resources
   */
  destroy() {
    this.pause();

    this.videoElements.forEach(video => {
      video.src = '';
      video.load();
    });
    this.videoElements.clear();
    this.clips = [];
  }
}

export default VideoCompositor;

